import type { Ref } from 'vue';
import { onBeforeMount, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { type controllerModel } from 'shared';

import { type ancillaryBaggageModel } from '@/widgets/ancillary-baggage';

import { type bookingStateModel } from '@/features/booking-state';

import { sentryModel } from '@/shared/model';
import { analyticsEventsLib } from '@/shared/lib';

import type { useProductsState } from './use-products-state';

type ProductsState = ReturnType<typeof useProductsState>;
type AncillariesStore = ReturnType<
  typeof ancillaryBaggageModel.useAncillariesStore
>;
type BookingStore = ReturnType<typeof bookingStateModel.useBookingStore>;
type ModalController = ReturnType<typeof controllerModel.useModal>;

export function useProductsPageEffects(options: {
  offerId: string;
  products: ProductsState;
  booking: {
    warnings: Ref<{ title: string; message: string }[]>;
    errors: Ref<string[]>;
  };
  bookingStore: BookingStore;
  modal: ModalController;
  ancillariesStore: AncillariesStore;
  checkSeatMapQuery: { data: Ref<{ available?: boolean } | undefined> };
  isProductsLoading: Ref<boolean>;
  t: (key: string) => string;
  updateProductsStateAtBookingStore: () => void;
}) {
  const router = useRouter();
  const {
    offerId,
    products,
    booking,
    bookingStore,
    modal,
    ancillariesStore,
    checkSeatMapQuery,
    isProductsLoading,
    t,
    updateProductsStateAtBookingStore,
  } = options;

  function onGoBack() {
    sessionStorage.removeItem(
      analyticsEventsLib.config.sessionStorageAnalyticsKeys.analytics,
    );
    sessionStorage.removeItem(
      analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
    );

    bookingStore.update({
      roaming: [],
    });
    router.back();
  }

  function onSelectPackage(e: unknown) {
    products.onPackageSelect(e);
    updateProductsStateAtBookingStore();
  }

  function onUnselectPackage() {
    products.onPackageUnselect();
    updateProductsStateAtBookingStore();
  }

  function handleNewSearchStart() {
    modal.hide('booking-expired-error-modal');
    router.replace({ name: 'MainPage' });
  }

  watch(isProductsLoading, newValue => {
    if (
      !newValue &&
      !sessionStorage.getItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
      )
    ) {
      sessionStorage.setItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
        'true',
      );
      analyticsEventsLib.productsPageOpen(
        {
          products: products.products.value,
          packages: products.packages.value,
          isBaggageAvailable: ancillariesStore.isBaggageAvailable,
          isSeatMapAvailable: Boolean(checkSeatMapQuery.data.value?.available),
        },
        bookingStore.offer?.$meta?.provider_class ?? '',
      );
    }
  });

  watch(booking.warnings, newState => {
    if (newState.length > 0) {
      modal.show('booking-warning-modal', {
        title: newState[0].title,
        message: newState[0].message,
      });
    }
  });

  watch(booking.errors, newState => {
    if (newState.length > 0) {
      analyticsEventsLib.bookingError(newState[0]);

      modal.show('booking-init-error-modal', {
        title: t('contacts.errors.attention'),
        message: newState[0],
      });

      sentryModel.captureException(
        sentryModel.getReadableError(
          sentryModel.createCustomErrorText(
            sentryModel.errorPrefix.DEFAULT,
            'FAILED_TO_CREATE_BOOKING',
          ),
          new Error('Failed to create booking'),
        ),
        { extra: { message: newState[0] } },
      );
    }
  });

  onMounted(() => {
    if (
      !sessionStorage.getItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.analytics,
      )
    ) {
      analyticsEventsLib.productsPageOpen(
        null,
        bookingStore.offer?.$meta?.provider_class ?? '',
      );
      sessionStorage.setItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.analytics,
        'true',
      );
    }
  });

  onBeforeMount(() => {
    products.init();
    ancillariesStore.initAncillaries(offerId);
  });

  return {
    onGoBack,
    onSelectPackage,
    onUnselectPackage,
    handleNewSearchStart,
  };
}
